import { LowStockLotField } from "../components/low-stock-lot-field";
import { LowStockIndicator } from "../components/low-stock-alerts";
//pkg-page.tsx

import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import { useEffect, useState, useContext } from "react";
import Title from "../components/title";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  Typography,
} from "@mui/material";
import LoadingBackdrop from "../components/loading-backdrop";
import { ErrorContext, UserContext } from "../components/app-routes";
import { pkgsApi } from "../services/api/pkgs";
import { Pkg, PkgLot } from "../models/pkg";
import { Grower } from "../models/grower";
import { DateTime } from "luxon";
import mixpanel from "mixpanel-browser";
import { goBackOrTo } from "../utils/history";
import PkgForm, { PkgFormValues } from "../components/pkg-form";
import { StripedDataGrid } from "../components/striped-data-grid";
import { toCurrency } from "./currency";
import {
  GridActionsCellItem,
  GridToolbar,
  GridRowClassNameParams,
  GridRenderCellParams,
  GridRowParams,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import * as yup from "yup";
import { Formik, Form, FastField, FieldProps } from "formik";
import { AppTextField } from "../components/text-field";
import { validationMessages } from "../utils/validation/messages";
import { pkgLotsApi } from "../services/api/pkg-lots";
import ConfirmDialog from "../components/confirm-dialog";
import { ActiveFilterControls } from "../components/active-filter-controls";

export interface PkgLotFormValues {
  number: string;
  supplier: string;
  archived: boolean;
  notes: string;
  dateReceived: string | null;
  qtyReceived: number | null;
  costOfLot: number | null;
  invoiceNumber: string;
  lowStockThreshold?: number | null;
}

const PkgLotDialog = (props: {
  open: boolean;
  actionLabel: string;
  grower: Grower | undefined;
  initialValues: PkgLotFormValues;
  onSubmit: (values: PkgLotFormValues) => void;
  onCancel: () => void;
}) => {
  return (
    <Dialog open={props.open} maxWidth={"sm"} fullWidth>
      <Formik
        initialValues={props.initialValues}
        validationSchema={yup.object({
          number: yup.string().required(validationMessages.Required),
          supplier: yup.string(),
          archived: yup.boolean(),
          notes: yup.string(),
        })}
        onSubmit={props.onSubmit}
      >
        {(form) => (
          <>
            <Form noValidate>
              <DialogTitle>Package Lot</DialogTitle>
              <DialogContent>
                <Box>
                  <FormControl
                    variant="standard"
                    error={
                      form.touched.archived && Boolean(form.errors.archived)
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="archived"
                          color="primary"
                          onChange={form.handleChange}
                          checked={form.values.archived}
                        />
                      }
                      label="Archived"
                    />
                    {form.touched.archived && form.errors.archived && (
                      <FormHelperText>{form.errors.archived}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
                <FastField name="number">
                  {(fieldProps: FieldProps) => (
                    <AppTextField {...fieldProps} autoFocus label="Number" />
                  )}
                </FastField>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <FastField
                      component={AppTextField}
                      name="supplier"
                      label="Supplier"
                      autoComplete="new-password"
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <FastField
                      component={AppTextField}
                      name="invoiceNumber"
                      label="Invoice #"
                      autoComplete="new-password"
                    />
                  </Box>
                </Box>
                {props.grower?.Plan === "pro" && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <FastField
                        component={AppTextField}
                        name="dateReceived"
                        label="Date Received"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <FastField
                        component={AppTextField}
                        name="costOfLot"
                        label={`Cost of Lot (${props.grower?.CountryISO || ""})`}
                        type="number"
                      />
                    </Box>
                  </Box>
                )}
                {props.grower?.Plan === "pro" && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 3 }}>
                      <FastField
                        component={AppTextField}
                        name="qtyReceived"
                        label="Qty Received"
                        type="number"
                      />
                    </Box>
                    <Box sx={{ flex: 3 }}></Box>
                  </Box>
                )}
                {props.grower?.Plan === "pro" && (
                  <LowStockLotField
                    seed={false}
                    defaultUnit={
                      props.grower.UnitSystem === "imperial" ? "lb" : "kg"
                    }
                  />
                )}
                <FastField name="notes">
                  {(fieldProps: FieldProps) => (
                    <AppTextField
                      {...fieldProps}
                      multiline
                      label="Notes"
                      rows={4}
                    />
                  )}
                </FastField>
              </DialogContent>
              <DialogActions>
                <Button variant="outlined" onClick={props.onCancel}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" type="submit">
                  {props.actionLabel}
                </Button>
              </DialogActions>
            </Form>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default function PkgPage() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [busy, setBusy] = useState(false);
  const [pkg, setPkg] = useState<Pkg | null>(null);
  const [pkgLots, setPkgLots] = useState<PkgLot[] | null>(null);
  const [pkgLotChangeId, setPkgLotChangeId] = useState<number>(0);
  const [saved, setSaved] = useState(false);
  const [showAddLotDialog, setShowAddLotDialog] = useState(false);
  const { handleError } = useContext(ErrorContext);
  const { grower } = useContext(UserContext);
  const [openConfirmTrashPkgLotDialog, setOpenConfirmTrashPkgLotDialog] =
    useState(false);
  const [pkgLotToAction, setPkgLotToAction] = useState<PkgLot | null>(null);
  const [openEditLotDialog, setOpenEditLotDialog] = useState(false);

  const showArchivedPkgLotsCheckedStorageKey = "pkgLotsShowArchivedChecked";
  const showActivePkgLotsCheckedStorageKey = "pkgLotsShowActiveChecked";

  const [showArchivedPkgLotsChecked, setShowArchivedPkgLotsChecked] = useState(
    window.localStorage.getItem(showArchivedPkgLotsCheckedStorageKey) !==
      false.toString(),
  );

  const [showActivePkgLotsChecked, setShowActivePkgLotsChecked] = useState(
    window.localStorage.getItem(showActivePkgLotsCheckedStorageKey) !==
      false.toString(),
  );

  useEffect(() => {
    async function getPkg() {
      if (!id || id === "add" || Number.isNaN(parseInt(id))) {
        setPkg({
          id: 0,
          name: "",
          size: "",
          type: "",
          notes: "",
          archivedAt: null,
          lots: [],
        });
        return;
      }

      setBusy(true);
      try {
        const pkg = await pkgsApi.getSingle(parseInt(id));
        setPkg(pkg);
      } catch (error) {
        handleError(error);
      } finally {
        setBusy(false);
      }
    }

    getPkg();
  }, [id]);

  useEffect(() => {
    async function getPkgLots() {
      if (!pkg) {
        return;
      }

      setBusy(true);
      try {
        const pkgLots = await pkgLotsApi.getAll(pkg);
        setPkgLots(pkgLots);
      } catch (error) {
        handleError(error);
      } finally {
        setBusy(false);
      }
    }
    getPkgLots();
  }, [pkg, pkgLotChangeId]);

  const handleSubmit = async (values: PkgFormValues) => {
    if (!pkg) {
      throw new Error("Invalid state");
    }

    setBusy(true);

    try {
      const pkgToSave = {
        ...pkg,
        name: values.name,
        size: values.size,
        notes: values.notes,
        archivedAt:
          !!pkg.archivedAt === values.archived
            ? pkg.archivedAt
            : values.archived
              ? DateTime.now().toISO()
              : null,
      };

      if (pkg.id === 0) {
        await pkgsApi.add(pkgToSave);
        mixpanel.track("Add Pkg");
      } else {
        await pkgsApi.update(pkgToSave);
        mixpanel.track("Update Pkg");
      }

      setSaved(true);

      goBackOrTo(history, "/home/pkgs");
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const onClickCancel = () => {
    goBackOrTo(history, "/home/pkgs");
  };

  const onClickAddLot = () => {
    setShowAddLotDialog(true);
  };

  const handleShowActivePkgLotsChanged = (checked: boolean) => {
    setShowActivePkgLotsChecked(checked);
    window.localStorage.setItem(
      showActivePkgLotsCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedPkgLotsChanged = (checked: boolean) => {
    setShowArchivedPkgLotsChecked(checked);
    window.localStorage.setItem(
      showArchivedPkgLotsCheckedStorageKey,
      checked.toString(),
    );
  };

  const onClickSetDefaultPkgLot = async (pkgLotId: number) => {
    if (!pkg || !pkgLots) {
      return;
    }

    const pkgLot = pkgLots.find((lot) => lot.id === pkgLotId);

    if (!pkgLot) {
      return;
    }

    try {
      await pkgLotsApi.update(pkg, {
        ...pkgLot,
        isDefault: true,
      });

      setPkgLotChangeId(pkgLotChangeId + 1);
    } catch (error) {
      handleError(error);
    }
  };

  const onSubmitAddLotDialog = async (values: PkgLotFormValues) => {
    if (!pkg) {
      throw new Error("Invalid state");
    }

    if (!grower) {
      throw new Error("Invalid UserContext");
    }
    setBusy(true);

    try {
      const activePkgLots = pkgLots?.filter((l) => l.archivedAt === null) || [];
      const defaultPkgLot = activePkgLots.find((l) => l.isDefault);

      if (activePkgLots.length === 1 && !defaultPkgLot) {
        await pkgLotsApi.update(pkg, {
          ...activePkgLots[0],
          isDefault: true,
        });
      }
      await pkgLotsApi.add(pkg, {
        id: 0,
        createdAt: null,
        number: values.number,
        supplier: values.supplier,
        invoiceNumber: values.invoiceNumber,
        lowStockThreshold: values.lowStockThreshold ?? null,
        archivedAt: values.archived ? DateTime.now().toISO() : null,
        notes: values.notes,
        isDefault: false,
        dateReceived: values.dateReceived
          ? DateTime.fromISO(values.dateReceived).toISO()
          : null,
        qtyReceived: values.qtyReceived,
        costOfLot:
          values.costOfLot != null
            ? toCurrency(values.costOfLot.toString(), grower.CountryISO)
                .intValue
            : null,
      });

      setPkgLotChangeId(pkgLotChangeId + 1);
      setShowAddLotDialog(false);

      mixpanel.track("Update Pkg Lot");
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const onCloseTrashPkgLotDialog = async (ok: boolean | undefined) => {
    if (!pkg || !pkgLotToAction) {
      throw new Error("Invalid state");
    }

    setOpenConfirmTrashPkgLotDialog(false);

    if (!ok) {
      return;
    }

    setBusy(true);

    try {
      await pkgLotsApi.trash(pkg, pkgLotToAction);
      setPkgLotChangeId(pkgLotChangeId + 1);
    } catch (error) {
      handleError(error);
    } finally {
      setPkgLotToAction(null);
      setBusy(false);
    }
  };

  const onClickTrashPkgLot = (id: number) => {
    if (!pkgLots) {
      throw new Error("Invalid state");
    }

    const pkgLot = pkgLots.find((v) => v.id === id);

    if (!pkgLot) {
      throw new Error("Invalid state");
    }

    setPkgLotToAction(pkgLot);
    setOpenConfirmTrashPkgLotDialog(true);
  };

  const onSubmitEditLotDialog = async (values: PkgLotFormValues) => {
    if (!pkg || !pkgLotToAction) {
      throw new Error("Invalid state");
    }

    setOpenEditLotDialog(false);

    if (!grower) {
      throw new Error("Invalid UserContext");
    }
    setBusy(true);

    try {
      await pkgLotsApi.update(pkg, {
        ...pkgLotToAction,
        number: values.number,
        supplier: values.supplier,
        invoiceNumber: values.invoiceNumber,
        lowStockThreshold: values.lowStockThreshold ?? null,
        archivedAt:
          pkgLotToAction.archivedAt && values.archived
            ? pkgLotToAction.archivedAt
            : values.archived
              ? DateTime.now().toISO()
              : null,
        dateReceived: values.dateReceived
          ? DateTime.fromISO(values.dateReceived).toISO()
          : null,
        qtyReceived: values.qtyReceived,
        costOfLot:
          values.costOfLot != null
            ? toCurrency(values.costOfLot.toString(), grower.CountryISO)
                .intValue
            : null,
        notes: values.notes,
      });
      setPkgLotChangeId(pkgLotChangeId + 1);
    } catch (error) {
      handleError(error);
    } finally {
      setPkgLotToAction(null);
      setBusy(false);
    }
  };

  const onClickEditPkgLot = (id: number) => {
    if (!pkgLots) {
      throw new Error("Invalid state");
    }

    const pkgLot = pkgLots.find((v) => v.id === id);

    if (!pkgLot) {
      throw new Error("Invalid state");
    }

    setPkgLotToAction(pkgLot);
    setOpenEditLotDialog(true);
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
      <Box>
        <Paper
          sx={{
            p: 2,
            display: "flex",
            overflow: "auto",
            flexDirection: "column",
          }}
        >
          <Title>Add Package</Title>
          {pkg && pkgLots && (
            <>
              <PkgForm
                initialValues={{
                  archived: !!pkg.archivedAt,
                  id: pkg.id,
                  name: pkg.name,
                  notes: pkg.notes,
                  size: pkg.size,
                }}
                saved={saved}
                onSubmit={handleSubmit}
              >
                <Box mt={2} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={onClickCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={busy}
                  >
                    Save
                  </Button>
                </Box>
              </PkgForm>
              {pkg.id === 0 ? (
                <Box mt={2}>
                  <Typography color="text.secondary">
                    Save this package, then re-open to add package lots.
                  </Typography>
                </Box>
              ) : (
                <Box mt={2}>
                  {" "}
                  <Typography variant="h5" color="black" gutterBottom>
                    Package Lots
                  </Typography>
                  <Button
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={onClickAddLot}
                  >
                    Add lot
                  </Button>
                  <ActiveFilterControls
                    showActiveChecked={showActivePkgLotsChecked}
                    showArchivedChecked={showArchivedPkgLotsChecked}
                    onChangeShowActive={handleShowActivePkgLotsChanged}
                    onChangeShowArchived={handleShowArchivedPkgLotsChanged}
                  />
                  {pkgLots.length > 0 && (
                    <StripedDataGrid
                      storageKey="package-lots-grid"
                      columns={[
                        {
                          field: "isDefault",
                          headerName: "Default",
                          width: 80,
                          sortable: false,
                          renderCell: (params: GridRenderCellParams) => (
                            <Radio
                              checked={Boolean(params.row.isDefault)}
                              disabled={Boolean(params.row.archived)}
                              onChange={() =>
                                onClickSetDefaultPkgLot(params.row.id)
                              }
                            />
                          ),
                        },
                        {
                          field: "number",
                          renderCell: (params: GridRenderCellParams) => (
                            <span>
                              {params.value}
                              <LowStockIndicator
                                kind="packaging"
                                lotId={Number(params.id)}
                              />
                            </span>
                          ),
                          headerName: "Number",
                          width: 200,
                        },
                        {
                          field: "supplier",
                          headerName: "Supplier",
                          width: 200,
                        },
                        {
                          field: "archived",
                          headerName: "Archived",
                          width: 100,
                          type: "boolean",
                        },
                        {
                          field: "createdAt",
                          headerName: "Created",
                          width: 200,
                          type: "dateTime",
                          renderCell: (params: GridRenderCellParams) => {
                            const value = params.value as Date | string | null;
                            if (!value) return "";

                            const dateObj =
                              typeof value === "string"
                                ? DateTime.fromISO(value)
                                : DateTime.fromJSDate(value);

                            return dateObj.toLocaleString(
                              DateTime.DATETIME_MED,
                            );
                          },
                        },
                        {
                          field: "notes",
                          headerName: "Notes",
                          width: 200,
                          type: "string",
                        },
                        {
                          field: "actions",
                          type: "actions",
                          headerName: "",
                          width: 100,
                          minWidth: 100,
                          cellClassName: "actions",
                          align: "right",
                          hideable: false,
                          getActions: (params: GridRowParams) => [
                            <GridActionsCellItem
                              icon={<EditIcon />}
                              label="Edit"
                              className="textPrimary"
                              onClick={() => onClickEditPkgLot(params.row.id)}
                              color="inherit"
                            />,
                          ],
                        },
                      ]}
                      rows={pkgLots
                        .filter((c) =>
                          c.archivedAt === null
                            ? showActivePkgLotsChecked
                            : showArchivedPkgLotsChecked,
                        )
                        .map((c) => ({
                          id: c.id,
                          number: c.number,
                          supplier: c.supplier,
                          archived: c.archivedAt ? true : false,
                          createdAt: DateTime.fromISO(c.createdAt!).toJSDate(),
                          notes: c.notes,
                          isDefault:
                            c.isDefault ||
                            (c.archivedAt === null &&
                              pkgLots.filter((l) => l.archivedAt === null)
                                .length === 1),
                        }))}
                      initialState={{
                        filter: undefined,
                        sorting: {
                          sortModel: [
                            {
                              field: "createdAt",
                              sort: "desc",
                            },
                          ],
                        },
                      }}
                      slots={{
                        toolbar: GridToolbar,
                      }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          csvOptions: {
                            allColumns: true,
                          },
                        },
                      }}
                      getRowClassName={(params: GridRowClassNameParams) => {
                        if (params.row.archived) {
                          return "archived-row";
                        }

                        return params.indexRelativeToCurrentPage % 2 === 0
                          ? "even"
                          : "odd";
                      }}
                      disableRowSelectionOnClick
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
      <LoadingBackdrop open={busy} />
      <PkgLotDialog
        actionLabel="Add"
        grower={grower}
        initialValues={{
          archived: false,
          number: "",
          supplier: "",
          invoiceNumber: "",
          notes: "",
          dateReceived: null,
          qtyReceived: null,
          costOfLot: null,
        }}
        onCancel={() => setShowAddLotDialog(false)}
        open={showAddLotDialog}
        onSubmit={onSubmitAddLotDialog}
      />
      {pkgLotToAction && (
        <PkgLotDialog
          actionLabel="Save"
          grower={grower}
          initialValues={{
            archived: !!pkgLotToAction.archivedAt,
            number: pkgLotToAction.number,
            supplier: pkgLotToAction.supplier,
            invoiceNumber: pkgLotToAction.invoiceNumber,
            lowStockThreshold: pkgLotToAction.lowStockThreshold ?? null,
            notes: pkgLotToAction.notes,
            dateReceived: pkgLotToAction.dateReceived
              ? DateTime.fromISO(pkgLotToAction.dateReceived).toISODate()
              : null,
            qtyReceived: pkgLotToAction.qtyReceived,
            costOfLot:
              pkgLotToAction.costOfLot != null && grower
                ? toCurrency(pkgLotToAction.costOfLot, grower.CountryISO).value
                : null,
          }}
          onCancel={() => setOpenEditLotDialog(false)}
          open={openEditLotDialog}
          onSubmit={onSubmitEditLotDialog}
        />
      )}
      <ConfirmDialog
        open={openConfirmTrashPkgLotDialog}
        description={`Are you sure you want to trash the pkg lot '${pkgLotToAction?.number}'?`}
        onClose={onCloseTrashPkgLotDialog}
        title="Trash Package Lot"
      />
    </Container>
  );
}
